import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckboxLine, FormField, FormGrid, HpButton, HpCardHeader } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Spinner } from '../../components/ui/icons';
import { domainErrorOf, fmtInt } from '../../lib/format';
import { useLeadErrorText } from './error-text';
import {
  invalidateLeads,
  useLeadSettings,
  useOwners,
  type LeadSettings,
  type Owner,
  type SettingsSaveInput,
} from './queries';

/** Longest activity template the API accepts (`MAX_ACTIVITY_TEMPLATE_LENGTH`). */
const MAX_TEMPLATE = 500;

/**
 * Card "Global rules": the router's switches (existing owner priority, fallback owner, the "Nová
 * poptávka" activity). Optimistic save with the loaded version; a stale save reloads the values.
 */
export function SettingsCard() {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const settings = useLeadSettings();
  const owners = useOwners();
  const save = useMutation(
    trpc.leads.settings.save.mutationOptions({
      onSuccess: invalidateLeads,
      // Somebody saved in between: load their values (the form remounts on the new version).
      onError: (err) => {
        if (domainErrorOf(err)?.code === 'LEAD_SETTINGS_STALE') void invalidateLeads();
      },
    }),
  );

  return (
    <section className="hp-card">
      <HpCardHeader title={t('general.settings.title')} subtitle={t('general.settings.subtitle')} icon="settings" />
      <ErrorAlert
        error={save.error ?? settings.error ?? owners.error}
        describe={describe}
        className="bo-alert--inset"
      />
      {settings.data ? (
        <SettingsForm
          key={settings.data.version}
          settings={settings.data}
          owners={owners.data}
          saving={save.isPending}
          onSave={(input) => save.mutate(input)}
        />
      ) : (
        <p className="hp-card__body">
          <Spinner>{t('common:actions.loading')}</Spinner>
        </p>
      )}
    </section>
  );
}

function SettingsForm({
  settings,
  owners,
  saving,
  onSave,
}: {
  settings: LeadSettings;
  owners: Owner[] | undefined;
  saving: boolean;
  onSave: (input: SettingsSaveInput) => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const canManage = useCan()('leads.manage');
  const [preferExistingOwner, setPreferExistingOwner] = useState(settings.preferExistingOwner);
  const [fallback, setFallback] = useState<number | null>(settings.fallbackOwnerUserPdId);
  const [createActivity, setCreateActivity] = useState(settings.createActivity);
  const [template, setTemplate] = useState(settings.activityTemplate);

  // The stored fallback may have been deactivated (or not be in the active list): keep it selectable so the value shows.
  const listed = owners?.some((o) => o.pdId === settings.fallbackOwnerUserPdId) ?? true;
  const storedFallbackInactive =
    settings.fallbackOwnerUserPdId !== null && (settings.fallbackOwnerActive === false || !listed);
  const fallbackInvalid = fallback !== null && fallback === settings.fallbackOwnerUserPdId && storedFallbackInactive;
  const templateTrimmed = template.trim();
  const dirty =
    preferExistingOwner !== settings.preferExistingOwner ||
    fallback !== settings.fallbackOwnerUserPdId ||
    createActivity !== settings.createActivity ||
    templateTrimmed !== settings.activityTemplate;
  const invalid = templateTrimmed.length === 0 || fallbackInvalid;

  return (
    <form
      className="hp-card__body bo-sections"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canManage || invalid || !dirty) return;
        onSave({
          expectedVersion: settings.version,
          preferExistingOwner,
          fallbackOwnerUserPdId: fallback,
          createActivity,
          activityTemplate: templateTrimmed,
        });
      }}
    >
      <div className="bo-sections">
        <CheckboxLine
          checked={preferExistingOwner}
          disabled={!canManage}
          onChange={(e) => setPreferExistingOwner(e.target.checked)}
        >
          {t('general.settings.field.preferExistingOwner')}
        </CheckboxLine>
        <small className="bo-hint">{t('general.settings.preferExistingOwnerHint')}</small>
      </div>
      <FormGrid>
        <FormField
          label={t('general.settings.field.fallbackOwnerUserPdId')}
          hint={fallbackInvalid ? undefined : t('general.settings.fallbackHint')}
        >
          <select
            value={fallback ?? ''}
            disabled={!canManage}
            onChange={(e) => setFallback(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">{t('general.settings.fallbackNone')}</option>
            {settings.fallbackOwnerUserPdId !== null && storedFallbackInactive && (
              <option value={settings.fallbackOwnerUserPdId}>
                {t('general.settings.fallbackInactiveOption', {
                  name: settings.fallbackOwnerName ?? t('rules.unknownId', { id: settings.fallbackOwnerUserPdId }),
                })}
              </option>
            )}
            {(owners ?? []).map((o) => (
              <option key={o.pdId} value={o.pdId}>
                {o.name}
              </option>
            ))}
            {owners === undefined && settings.fallbackOwnerUserPdId !== null && !storedFallbackInactive && (
              <option value={settings.fallbackOwnerUserPdId}>
                {settings.fallbackOwnerName ?? t('rules.unknownId', { id: settings.fallbackOwnerUserPdId })}
              </option>
            )}
          </select>
        </FormField>
      </FormGrid>
      {fallbackInvalid && <p className="bo-hint bo-hint--danger">{t('general.settings.fallbackInactiveHint')}</p>}
      <div className="bo-sections">
        <CheckboxLine
          checked={createActivity}
          disabled={!canManage}
          onChange={(e) => setCreateActivity(e.target.checked)}
        >
          {t('general.settings.field.createActivity')}
        </CheckboxLine>
        <FormGrid>
          <FormField
            full
            label={t('general.settings.field.activityTemplate')}
            hint={t('general.settings.templateHint', { max: fmtInt(MAX_TEMPLATE) })}
          >
            <textarea
              rows={2}
              maxLength={MAX_TEMPLATE}
              value={template}
              disabled={!canManage}
              onChange={(e) => setTemplate(e.target.value)}
            />
          </FormField>
        </FormGrid>
        {templateTrimmed.length === 0 && (
          <p className="bo-hint bo-hint--danger">{t('general.settings.templateEmpty')}</p>
        )}
      </div>
      <div className="bo-row bo-row--between">
        <small className="bo-muted">
          {t('general.settings.version', { version: fmtInt(settings.version) })}
          {' · '}
          {t('general.settings.changed')} <RelativeTime value={settings.updatedAt} />
          {settings.updatedBy && (
            <>
              {' '}
              {t('general.settings.changedBy', {
                name: settings.updatedBy.name ?? t('general.settings.unknownUser'),
              })}
            </>
          )}
        </small>
        {canManage ? (
          <HpButton type="submit" variant="primary" icon="check" busy={saving} disabled={invalid || !dirty}>
            {t('common:actions.save')}
          </HpButton>
        ) : (
          <small className="bo-hint">{t('general.readOnly')}</small>
        )}
      </div>
    </form>
  );
}
